import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { Crown, Lock } from 'lucide-react'
import { scorePrediction, applyMultipliers } from '../scoring'
import { cleanSweepGameValue } from '../season'
import { tierForPoints, type GameTier } from '../gameTier'
import { prefersReducedMotion, easeOutCubic } from '../scoreCountUp'
import { allocateParticles, releaseParticles } from '../particleBudget'
import { getClubAbbr } from '../clubBanners'
import { displayMinute } from '../app/matchClock'
import { igniteAngleFor } from './gameweekGrid/useEntryIgnite'
import { ResultTag } from './gameCard/ResultTag'
import {
  ExactRestingParticles,
  ExactSurfaceLayers,
  IDLE_PARTICLE_COST,
} from './gameCard/exactLayers'
import { formatKickoffLabel } from './gameCard/kickoffLabel'
import type { RevealCategory } from '../revealSequence'
import type { Game, Prediction } from '../types'
import '../styles/ignite.css'
import './GameCard.css'

const ONE_DAY_MS = 24 * 60 * 60 * 1000
const TWO_HOURS_MS = 2 * 60 * 60 * 1000
const COUNTDOWN_REFRESH_MS = 60 * 1000


interface ActualResult {
  homeScore: number
  awayScore: number
}

/** Card-local view of the full-time reveal, driven entirely by GameweekGrid's
 * conductor. The card owns nothing that reaches beyond its own
 * box — the spotlight, shockwaves and particle burst live in the grid's
 * effects layer. `pending` = scheduled but not yet its turn (held hidden);
 * `active` = playing its land + category flourish now. Once the grid drops the
 * prop, the card is simply "settled" (Phase B ambient for exact scores). */
export interface CardReveal {
  category: RevealCategory
  phase: 'pending' | 'active'
}

interface GameCardProps {
  game: Game
  prediction: Prediction | undefined
  actualResult: ActualResult | undefined
  onClick: () => void
  isBanker: boolean
  isDoubleUpActive: boolean
  isCleanSweepActive: boolean
  /** Whether the whole gameweek's Clean Sweep has cleared (every fixture's outcome called
   * correctly) — only meaningful when `isCleanSweepActive`. A missed sweep zeroes this card's
   * points regardless of how well this individual fixture was called. */
  cleanSweepCleared?: boolean
  /** True while the fixture is being played — `live` OR `halftime`. A live card is
   *  never sealed, and carries the live colour, score and the pick row. */
  isLive?: boolean
  liveResult?: ActualResult
  /** The server's status for this fixture, or `null`/absent when the live state
   *  came from the mock sim — which has neither status nor minute, and keeps the plain "LIVE"
   *  badge it has always had. */
  liveStatus?: 'live' | 'halftime' | null
  /** The provider's minute at the last poll; `null` when it reports none, which renders "—" rather
   *  than a fabricated number. */
  liveMinute?: number | null
  /** Milliseconds since that poll, ticking. Anchored once in the grid — a per-card clock would
   *  mean one interval per fixture. */
  liveElapsedMs?: number
  isEntering?: boolean
  deadlinePassed?: boolean
  reveal?: CardReveal
  /** This card is the one holding the stage — lift it above its neighbours.
   * Nothing dims any more (ticket 0418): the grid's bloom brightens them. */
  spotlit?: boolean
  /** Volleyed exact scores (3+ in one view) keep the surface and the light but
   * drop the looping particles, so three of them at once don't swarm the grid. */
  subdued?: boolean
  /** This card's position in the entry ignite wave — sets the
   * ring's hue offset so the grid isn't ten identical rings. The card still
   * decides whether it may ring at all (predicted, not sealed, not live). */
  igniteIndex?: number
  /** True for the ~590ms this card's row is flashing; the grid's wave owns the
   * stagger and clears it again afterwards. */
  igniteFiring?: boolean
  /** Frozen instance, rendered as a teaching visual on the rules screen. Everything
   * that would make the card live — the sealed countdown's minute tick, the onscreen observer, the
   * settled-exact embers — is switched off, so the card holds one still frame. The state itself
   * still comes from the same props the real grid passes, which is the whole point of reusing the
   * component instead of forking a picture of it. */
  demo?: boolean
  /** False when the card shows *someone else's* prediction — another player's gameweek detail.
   * "YOUR PICK" is reserved for the viewer's own picks; theirs read "PLAYER PICK",
   *  the same wording their reveal grid uses. */
  isOwnPick?: boolean
  /** Open this fixture on the Fixtures page. Set only where that destination
   *  exists — the predict grid. A card that is live or settled becomes pressable and calls this
   *  instead of staying inert; a *sealed* card (kicked off, no result yet) has nothing to show and
   *  stays disabled either way. Absent on the history grid and another player's reveal, which keeps
   *  those grids exactly as they were. */
  onOpenFixture?: () => void
}

const GameCard = forwardRef<HTMLButtonElement, GameCardProps>(function GameCard(
  {
    game,
    prediction,
    actualResult,
    onClick,
    isBanker,
    isDoubleUpActive,
    isCleanSweepActive,
    cleanSweepCleared = false,
    isLive = false,
    liveResult,
    liveStatus = null,
    liveMinute = null,
    liveElapsedMs = 0,
    isEntering = false,
    deadlinePassed = false,
    reveal,
    spotlit = false,
    subdued = false,
    igniteIndex = 0,
    igniteFiring = false,
    demo = false,
    isOwnPick = true,
    onOpenFixture,
  },
  forwardedRef
) {
  const isPredicted = prediction !== undefined
  const isAuto = prediction?.isAutoPredicted ?? false
  const isFinished = actualResult !== undefined
  const kickoffMs = new Date(game.kickoff).getTime()
  const kickoffLabel = formatKickoffLabel(game.kickoff)
  const pickLabel = isOwnPick ? 'YOUR PICK' : 'PLAYER PICK'

  // Merge the grid's forwarded ref (used to measure this card's rect when
  // positioning grid-level effects over it) with the local ref the
  // IntersectionObserver needs.
  const cardRef = useRef<HTMLButtonElement | null>(null)
  const setCardRef = useCallback(
    (el: HTMLButtonElement | null) => {
      cardRef.current = el
      if (typeof forwardedRef === 'function') forwardedRef(el)
      else if (forwardedRef) forwardedRef.current = el
    },
    [forwardedRef]
  )

  // Ticks once a minute, but only while this card is actually sealed — cards
  // that never reach that state (or have already moved past it) shouldn't
  // pay for a per-minute re-render.
  const [now, setNow] = useState(() => Date.now())
  const isSealed = deadlinePassed && !isLive && !isFinished
  // Past the deadline a live or settled card stops being dead and becomes a way into
  // its fixture. Sealed stays inert (nothing to show yet), and before the deadline every card keeps
  // opening the score editor — `deadlinePassed` is what separates the two, exactly as `disabled`
  // did before.
  const opensFixture = onOpenFixture !== undefined && deadlinePassed && (isLive || isFinished)
  const handlePress = opensFixture ? onOpenFixture : deadlinePassed ? undefined : onClick
  useEffect(() => {
    if (!isSealed || demo) return
    const intervalId = setInterval(() => setNow(Date.now()), COUNTDOWN_REFRESH_MS)
    return () => clearInterval(intervalId)
  }, [isSealed, demo])

  // Settled-card scoring, computed unconditionally so the reveal hooks below
  // (which must run every render) can see the final value. Meaningful only when
  // `isFinished`; otherwise the guards keep it at 0.
  const finishedBasePoints =
    isFinished && prediction ? scorePrediction(prediction, actualResult).points : 0
  const finishedIsExact =
    isFinished && prediction
      ? actualResult.homeScore === prediction.homeScore &&
        actualResult.awayScore === prediction.awayScore
      : false
  const finishedFinalPoints = isCleanSweepActive
    ? cleanSweepCleared
      ? cleanSweepGameValue(finishedIsExact, isBanker, isAuto)
      : 0
    : applyMultipliers(finishedBasePoints, isBanker, isDoubleUpActive)
  const finishedWasMultiplied = !isCleanSweepActive && finishedFinalPoints !== finishedBasePoints
  // Clean Sweep replaces the normal 5/3/1 tiering entirely: a missed sweep zeroes every
  // card regardless of how well that individual fixture was called (a cleared sweep implies
  // every fixture's outcome was correct, so 'muted'/plain-zero base tiers can't occur here).
  // An auto pick is the exception in both directions — capped at 2, it stays in the partial
  // band whatever the chip did, so the card never celebrates a pick nobody made.
  const finishedTier: GameTier =
    isCleanSweepActive && !isAuto
      ? cleanSweepCleared
        ? finishedIsExact
          ? 'win'
          : 'blue'
        : 'zero'
      : isCleanSweepActive && !cleanSweepCleared
        ? 'zero'
        : tierForPoints(finishedBasePoints)

  // IntersectionObserver (hard requirement 2): drop the loop-driving class when
  // the card scrolls offscreen so the live heartbeat, Phase B breathing, and
  // embers all pause.
  const [isOnscreen, setIsOnscreen] = useState(true)
  useEffect(() => {
    const el = cardRef.current
    if (!el || demo || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      ([entry]) => setIsOnscreen(entry.isIntersecting),
      { threshold: 0 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [demo])

  // Reveal points roll: count up 0 → final while this card's
  // flourish is active, starting at the slam for an exact score or right after
  // the land for the quieter tiers. `null` means "not rolling — show the final
  // value" (also the reduced-motion path).
  const revealPhase = reveal?.phase
  const revealCategory = reveal?.category
  const [rolledPoints, setRolledPoints] = useState<number | null>(null)
  useEffect(() => {
    if (revealPhase !== 'active') {
      setRolledPoints(null)
      return
    }
    if (prefersReducedMotion()) {
      setRolledPoints(null)
      return
    }
    const isExact = revealCategory === 'exact'
    // Exact rolls during the opening spotlight beat (starts just after the
    // slam); the quieter tiers roll right after their land.
    const rollStart = isExact ? 500 : 150
    const rollDuration = isExact ? 900 : 300
    setRolledPoints(0)
    let rafId = 0
    const startTimer = setTimeout(() => {
      const startTime = performance.now()
      const tick = (t: number) => {
        const progress = Math.min((t - startTime) / rollDuration, 1)
        setRolledPoints(Math.round(easeOutCubic(progress) * finishedFinalPoints))
        if (progress < 1) rafId = requestAnimationFrame(tick)
      }
      rafId = requestAnimationFrame(tick)
    }, rollStart)
    return () => {
      clearTimeout(startTimer)
      cancelAnimationFrame(rafId)
    }
  }, [revealPhase, revealCategory, finishedFinalPoints])

  // The settled card's looping particles, subject to the global budget. Skipped
  // for volleyed cards, while still mid-reveal, offscreen (IntersectionObserver
  // pause), or under reduced motion. The rest of the resting state — surface,
  // rim, specular pass, elevation — is CSS and always on.
  const wantsIdleParticles =
    isFinished &&
    finishedTier === 'win' &&
    isOnscreen &&
    reveal === undefined &&
    !subdued &&
    !demo &&
    !prefersReducedMotion()
  const [particleShare, setParticleShare] = useState(0)
  useEffect(() => {
    if (!wantsIdleParticles) {
      setParticleShare(0)
      return
    }
    const grant = allocateParticles(IDLE_PARTICLE_COST)
    setParticleShare(grant / IDLE_PARTICLE_COST)
    return () => releaseParticles(grant)
  }, [wantsIdleParticles])

  if (isFinished) {
    const displayPoints = rolledPoints !== null ? rolledPoints : finishedFinalPoints

    const finishedClassName = [
      'game-card',
      'game-card--finished',
      `game-card--tier-${finishedTier}`,
      !isOnscreen ? 'game-card--offscreen' : null,
      reveal?.phase === 'pending' ? 'game-card--reveal-pending' : null,
      reveal?.phase === 'active' ? 'game-card--revealing' : null,
      reveal?.phase === 'active' ? `game-card--reveal-${reveal.category}` : null,
      spotlit ? 'game-card--spotlit' : null,
      opensFixture ? 'game-card--opens-fixture' : null,
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <button
        ref={setCardRef}
        className={finishedClassName}
        onClick={handlePress}
        disabled={deadlinePassed && !opensFixture}
      >
        {/* The settled exact card's resting state (ticket 0418) — what it looks
            like every time the app is opened, for the rest of the season. */}
        {finishedTier === 'win' && (
          <>
            <ExactSurfaceLayers ring="locked" />
            <ExactRestingParticles share={particleShare} />
          </>
        )}
        {isBanker && (
          <span className="game-card__banker-badge">
            <Crown aria-hidden="true" />
          </span>
        )}
        <ResultTag tier={finishedTier} isAuto={isAuto} />
        <span className="game-card__row">
          <span className="game-card__team">{getClubAbbr(game.homeTeam)}</span>
          <span className="game-card__score">
            {actualResult.homeScore}-{actualResult.awayScore}
          </span>
          <span className="game-card__team">{getClubAbbr(game.awayTeam)}</span>
        </span>
        <span className="game-card__result-divider">
          <span className="game-card__your-pick">
            {prediction
              ? `${pickLabel} ${prediction.homeScore}-${prediction.awayScore}`
              : 'No prediction'}
          </span>
          <span className="game-card__points">
            {finishedWasMultiplied && <span className="game-card__points-base">{finishedBasePoints} → </span>}
            {displayPoints} pts
          </span>
        </span>
      </button>
    )
  }

  // While live, the card carries the same tier colour language (win/blue/muted/
  // zero) — it's just as eligible to change as the score itself, right up to
  // full time. What's reserved for the actual whistle (the isFinished branch
  // above, scoped in CSS to .game-card--finished) is every terminal gesture:
  // the permanent rim, the dim, the check stamp, the celebratory glint. A live
  // card may glow (the live exact-score state) but never celebrates and never
  // recedes.
  const livePoints =
    isLive && liveResult && prediction ? scorePrediction(prediction, liveResult).points : undefined
  const liveTier = livePoints === undefined ? undefined : tierForPoints(livePoints)
  const isLiveExact = isLive && liveTier === 'win'

  // The minute slot, from the one shared clock: "23'", "45+", "HT", or "—" when
  // the provider reports no minute. `null` means there is no minute to show at all — the mock sim,
  // which has never had one.
  const minuteLabel = liveStatus === null ? null : displayMinute(liveStatus, liveMinute, liveElapsedMs)

  // Countdown granularity tightens as kickoff approaches: most
  // gameweeks share a kickoff time across several fixtures, so a single "Xh Ym"
  // band made the whole grid read identically and tick over at once. Bands are
  // half-open and measured off the same 60s tick — date/time at 24h and beyond,
  // whole hours from 2h to 24h, bare minutes inside the last two.
  const msUntilKickoff = kickoffMs - now
  const countdownMinutesTotal = Math.floor(msUntilKickoff / 60000)
  const countdownLabel =
    msUntilKickoff <= 0
      ? 'Kicking off…'
      : msUntilKickoff < TWO_HOURS_MS
        ? `${countdownMinutesTotal}m`
        : msUntilKickoff < ONE_DAY_MS
          ? `${Math.floor(countdownMinutesTotal / 60)}h`
          : kickoffLabel

  // Entry ignite (replacing the comet span): the ring is a
  // pseudo-element on the card itself, so this is a class rather than an
  // element. It marks the cards the grid's wave MAY light — a prediction the
  // user can still change — and sits invisible until it does.
  const canIgnite = isPredicted && !isSealed && !isLive && !isFinished

  return (
    <button
      ref={setCardRef}
      className={`game-card ${isPredicted ? 'game-card--predicted' : ''} ${
        isAuto ? 'game-card--auto' : ''
      } ${isLive ? 'game-card--live' : ''} ${isEntering ? 'game-card--entering' : ''} ${
        isSealed ? 'game-card--sealed' : ''
      } ${liveTier ? `game-card--tier-${liveTier}` : ''} ${
        isLiveExact ? 'game-card--live-exact' : ''
      } ${!isOnscreen ? 'game-card--offscreen' : ''} ${canIgnite ? 'ignite' : ''} ${
        canIgnite && igniteFiring ? 'ignite--firing' : ''
      } ${opensFixture ? 'game-card--opens-fixture' : ''}`}
      style={
        canIgnite
          ? ({ '--ignite-angle': igniteAngleFor(igniteIndex) } as CSSProperties)
          : undefined
      }
      onClick={handlePress}
      disabled={deadlinePassed && !opensFixture}
    >
      {/* The live exact score gets the settled card's surface and light, and
          none of its celebration — nothing has been won yet. */}
      {isLiveExact && <ExactSurfaceLayers ring="live" />}
      {isBanker && (
        <span className="game-card__banker-badge">
          <Crown aria-hidden="true" />
        </span>
      )}
      {/* Sealed marker — a small opposite-corner glyph to the
          Banker crown, not the card's headline. The prediction owns the score
          slot now. Gated on isSealed (never live, never finished) so the live
          treatment alone carries the state once the match is under way. */}
      {isSealed && (
        <span className="game-card__lock-icon">
          <Lock aria-hidden="true" />
        </span>
      )}
      {isSealed ? (
        <span className="game-card__time game-card__time--sealed">{countdownLabel}</span>
      ) : (
        <span className="game-card__time">
          {isLive ? (
            <span className="game-card__live-badge">
              <span className="game-card__live-dot" aria-hidden="true" />
              {/* Half time replaces the word rather than sitting beside it: "LIVE HT" reads as
                  two contradictory claims. */}
              {liveStatus !== 'halftime' && 'LIVE'}
              {minuteLabel !== null && (
                <span className="game-card__live-minute">{minuteLabel}</span>
              )}
            </span>
          ) : isAuto ? (
            'AUTO'
          ) : (
            kickoffLabel
          )}
        </span>
      )}
      <span className="game-card__row">
        <span className="game-card__team">{getClubAbbr(game.homeTeam)}</span>
        {/* Sealed cards render their prediction here too — the
            held pick occupies the same slot a live score does, dimmed by
            .game-card--sealed .game-card__score so it never reads as a result.
            An unpredicted sealed fixture keeps the placeholder. */}
        <span className="game-card__score">
          {isLive && liveResult
            ? `${liveResult.homeScore}-${liveResult.awayScore}`
            : isPredicted
              ? `${prediction!.homeScore}-${prediction!.awayScore}`
              : '– : –'}
        </span>
        <span className="game-card__team">{getClubAbbr(game.awayTeam)}</span>
      </span>
      {isLive && isPredicted && (
        <span className="game-card__result-divider game-card__result-divider--live">
          <span className="game-card__your-pick">
            {pickLabel} {prediction!.homeScore}-{prediction!.awayScore}
          </span>
          {livePoints !== undefined && livePoints > 0 && (
            <span className="game-card__points">{livePoints} pts</span>
          )}
        </span>
      )}
    </button>
  )
})

export default GameCard
